"use client";
import NextLink from "next/link";
import { styled } from "@mui/material/styles";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";
import { DateTime } from "luxon";
import { externalUrls, getUrls } from "@portal/app/_common/lib/urls";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { DashFooter } from "@portal/app/dash/_components/DashFooter";
import { CustomerNavBar } from "@portal/app/_components/ui-elements/CustomerNavBar";
import { forwardRef } from "react";
import { FooterDisclaimer } from "./FooterDisclaimer";
import { LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT } from "./layoutBreakpoints";

export { LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT } from "./layoutBreakpoints";
interface FooterLinkProps {
  href: string;
  title: string;
}

const FooterNavLink = ({ href, title }: FooterLinkProps) => (
  <NextLink href={href} className="footerNavLink">
    {title}
  </NextLink>
);

const FooterSmallLink = ({ href, title }: FooterLinkProps) => (
  <NextLink href={href} className="footerSmallLink">
    {title}
  </NextLink>
);

const ExternalFooterSmallLink = ({ title, ...propsToAnchor }: FooterLinkProps) => (
  <a className="footerSmallLink" {...propsToAnchor}>
    {title}
  </a>
);

type Footer = "MAIN" | "DASH" | "BOTH" | "NONE";

export const Layout = forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    dreamSearch?: boolean;
    footer?: Footer;
    nav?: React.ReactElement<unknown> | null;
    showDisclaimer?: boolean;
  }
>(({ children, dreamSearch, footer = "MAIN", nav, showDisclaimer = true }, ref) => {
  const urls = getUrls();
  const { clientLocation } = usePageContext();
  const currentYear = DateTime.local().year;

  return (
    <>
      <LayoutContainer $dreamSearch={dreamSearch} $footer={footer} ref={ref}>
        {nav === undefined ? <CustomerNavBar /> : nav}
        {children}
        {(footer === "DASH" || footer === "BOTH") && <DashFooter />}
        {(footer === "MAIN" || footer === "BOTH") && (
          <Footer data-testid="layout-footer">
            <FooterContent>
              <FooterTop>
                <FooterBigLinks>
                  <FooterNavLink
                    href={urls.DreamSearch({ industry: "HEALTH", zip: clientLocation.zip })}
                    title="Search Programs"
                  />
                </FooterBigLinks>
                <FooterColumns>
                  <FooterColumn>
                    <FooterColumnLabel>Company</FooterColumnLabel>
                    <FooterSmallLink href={urls.Jobs()} title="Work at Dreambound" />
                    <ExternalFooterSmallLink href={urls.BlogHome()} title="Blog" />
                  </FooterColumn>
                  <FooterColumn>
                    <FooterColumnLabel>Connect</FooterColumnLabel>
                    <FooterSmallLink href={urls.Schools()} title="Partner with Us" />
                    <ExternalFooterSmallLink href={externalUrls.Facebook} title="Facebook" />
                    <ExternalFooterSmallLink
                      href={externalUrls.Twitter}
                      title="X (formerly Twitter)"
                    />
                  </FooterColumn>
                </FooterColumns>
              </FooterTop>
              <FooterWordmark>
                <img src="/logo-lockup-dark.svg" alt="Dreambound" />
              </FooterWordmark>
              <FooterBottom>
                <FooterLegal>
                  <span>Copyright &copy; {currentYear} Dreambound Inc. All rights reserved.</span>
                  <FooterLegalLinks>
                    <NextLink href={urls.LegalTermsAndConditions()}>Terms and Conditions</NextLink>
                    <NextLink href={urls.LegalPrivacy()}>Privacy Policy</NextLink>
                    <a href={externalUrls.DoNotSellMyInfo}>
                      Do Not Sell or Share My Personal Information
                    </a>
                  </FooterLegalLinks>
                </FooterLegal>
                {showDisclaimer && <FooterDisclaimer />}
              </FooterBottom>
            </FooterContent>
          </Footer>
        )}
      </LayoutContainer>
    </>
  );
});

const LayoutContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "$dreamSearch" && prop !== "$footer",
})<{ $dreamSearch?: boolean; $footer?: Footer }>(
  ({ theme, $dreamSearch, $footer }) => `
  ${theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    position: relative;
  }

  display: flex;
  flex-direction: column;
  min-height: 100%;

  ${
    $dreamSearch
      ? `
    ${theme.breakpoints.down("md")} {
      .logo,
      footer {
        display: none;
      }
    }
  `
      : ""
  }

  ${
    $footer === "BOTH" || $footer === "DASH"
      ? `
    ${theme.breakpoints.down("md")} {
      padding-bottom: ${theme.mixins.toolbar.minHeight}px;
    }
  `
      : ""
  }

  // Standardize page title sizes
  h1.pageTitle {
    font-size: 38px;
    line-height: 1.2;
    font-weight: 600;

    ${theme.breakpoints.down("xs")} {
      font-size: 24px;
    }
  }

  button.ctaButton,
  a.ctaButton {
    display: inline-block;
    border: none;
    padding: 12px 20px 14px 20px;

    background: ${theme.palette.primary.dark};
    color: #fff;
    font-weight: 700;
    font-size: 18px;
    line-height: 1;
    border-radius: 3px;
    cursor: pointer;
    text-decoration: none;

    ${theme.breakpoints.down("xs")} {
      display: block;
      width: 100%;
      font-size: 16px;
      text-align: center;
    }

    &:hover {
      background: ${theme.palette.error.main};
    }

    &:focus {
      outline: none;
    }

    svg {
      margin-left: 6px;
      margin-right: -4px;
      font-size: 80%;
    }
  }
`
);

const Footer = styled("footer")`
  background: #d8e8f8 url("/motion-blur.webp") no-repeat center center;
  background-size: cover;
  color: ${paletteOptions.primary.main};
`;

const FooterContent = styled("div")`
  max-width: 75rem;
  margin: 0 auto;
  padding: 5rem 2rem 3rem;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 3rem 1.5rem 2rem;
  }
`;

const FooterTop = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4rem;
  margin-bottom: 4rem;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-direction: column;
    gap: 3rem;
    margin-bottom: 3rem;
  }
`;

const FooterBigLinks = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .footerNavLink {
    font-family: var(--font-heading), Georgia, serif;
    font-size: 3rem;
    font-weight: 700;
    text-transform: uppercase;
    color: ${paletteOptions.primary.main};
    text-decoration: none;
    line-height: 1.1;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    .footerNavLink {
      font-size: 2.25rem;
    }
  }
`;

const FooterColumns = styled("div")`
  display: flex;
  gap: 4rem;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    gap: 3rem;
  }
`;

const FooterColumn = styled("div")`
  display: flex;
  flex-direction: column;

  .footerSmallLink {
    color: ${paletteOptions.primary.main};
    text-decoration: none;
    font-size: 1rem;
    line-height: 2;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }
`;

// Decorative column labels, not document structure — kept out of the heading outline.
const FooterColumnLabel = styled("p")`
  margin: 0 0 1rem 0;
  color: ${paletteOptions.primary.main};
  opacity: 0.7;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const FooterWordmark = styled("div")`
  padding: 3rem 0;
  border-top: 1px solid rgba(4, 41, 158, 0.25);

  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    opacity: 1;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 2rem 0;
  }
`;

const FooterBottom = styled("div")`
  border-top: 1px solid rgba(4, 41, 158, 0.25);
  padding-top: 2rem;
`;

const FooterLegal = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
  color: ${paletteOptions.primary.main};
  font-size: 0.8rem;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-direction: column;
  }
`;

const FooterLegalLinks = styled("div")`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  a {
    color: ${paletteOptions.primary.main};
    text-decoration: none;
    font-size: 0.8rem;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }
`;
